import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";

const SettingsContext = createContext(null);

const DEFAULT_LABELS = {
  gift_boxes: "Gift boxes",
  reorder: "Reorder",
  valuation: "Valuation",
};

const FALLBACK = {
  modules: { gift_boxes: true, reorder: true, valuation: true },
  labels: DEFAULT_LABELS,
  business_name: "Inventory",
  currency_symbol: "$",
  currency_code: "USD",
  markup_multiplier: 2.5,
  low_stock_threshold: 3,
  brand_color: "#8E2A5B",
  logo_url: "",
  gift_box_types: [],
};

/** Settings and categories for whichever client is currently open. */
const CACHE_PREFIX = "settings_cache:";

/** Last-known settings for a client, so a refresh doesn't start from defaults. */
function readCache(tenantId) {
  if (!tenantId) return null;
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + tenantId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(tenantId, value) {
  if (!tenantId) return;
  try {
    window.localStorage.setItem(CACHE_PREFIX + tenantId, JSON.stringify(value));
  } catch {
    /* private browsing */
  }
}

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  // Starting from the cached values means the sidebar doesn't briefly show
  // features this client has switched off.
  const [settings, setSettings] = useState(() => readCache(tenantId) || FALLBACK);
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(() => !!readCache(tenantId));

  // Which client the newest request was for. A reply that arrives after we've
  // moved on must be ignored — otherwise a slow answer for the client you just
  // left overwrites the one you're now in, and their name, prefix and
  // categories appear under the wrong business.
  const currentTenant = useRef(tenantId);

  // Switching client swaps to that client's cached settings straight away.
  useEffect(() => {
    currentTenant.current = tenantId;
    const cached = readCache(tenantId);
    setSettings(cached || FALLBACK);
    // Categories aren't cached, so they must be cleared rather than left
    // showing the previous client's.
    setCategories([]);
    setLoaded(!!cached);
  }, [tenantId]);

  const refresh = useCallback(async () => {
    if (!user || !tenantId) {
      setSettings(FALLBACK);
      setCategories([]);
      setLoaded(false);
      return;
    }
    const askedFor = tenantId;
    try {
      const [s, c] = await Promise.all([api.get("/settings"), api.get("/categories")]);
      if (currentTenant.current !== askedFor) return;
      const merged = {
        ...FALLBACK,
        ...s.data,
        modules: { ...FALLBACK.modules, ...(s.data.modules || {}) },
        labels: { ...DEFAULT_LABELS, ...(s.data.labels || {}) },
      };
      setSettings(merged);
      setCategories(c.data);
      writeCache(askedFor, merged);
      setLoaded(true);
    } catch {
      /* keep whatever we have so the shell still renders */
    }
  }, [user, tenantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Small helpers so screens don't each re-implement the same lookups.
  const isOn = (key) => settings.modules?.[key] !== false;
  const labelFor = (key) => settings.labels?.[key] || DEFAULT_LABELS[key] || key;

  return (
    <SettingsContext.Provider
      value={{ settings, setSettings, categories, refresh, isOn, labelFor, loaded }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
